// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — a headless "player" for the balance sim (tests/balance) and the browser test
// hook. It is TWO independent things a real player is: a BANKING POLICY (when to
// release — the push-your-luck decision) and a HAND (a seeded motion generator: some
// baseline jitter, plus a startle spike after a jolt, scaled by how steady the player
// is). Both drive the EXACT shipped core (step/release), so a sim result is the game's
// result. The load-bearing checks the sim makes:
//   · a READER (banks on the agitation tell) beats a BLIND banker (fixed charge target,
//     tell ignored) — the tell is load-bearing;
//   · for a SHAKY hand, banking early (avoiding the late jolt cluster) beats riding to
//     the brim — the flinch (spill) risk is load-bearing; that edge inverts for a
//     perfectly STEADY hand, where riding longer simply earns more.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import { createGame, release, step, type GameState } from './game';
import type { Mode } from './modes';

export interface BotPolicy {
  /** Release when agitation ≥ this (a "reader" using the visible tell). */
  bankAgitation: number;
  /** If set, ignore the tell and release at this raw charge instead (a "blind" banker). */
  bankPot?: number;
  /** 0 = shaky (big startle spikes), 1 = rock-steady (barely moves). */
  steadiness: number;
  /** How strongly a jolt spikes the generated motion (0..1). */
  startleReact: number;
}

const DT = 1000 / 60;

/** The banking decision for the current tick. */
export function decideBank(s: GameState, p: BotPolicy): boolean {
  if (!s.holding) return false;
  if (s.potMs <= 0) return false; // never bank an orb that has not begun to swell
  if (p.bankPot !== undefined) return s.pot >= p.bankPot;
  return s.agitation >= p.bankAgitation;
}

/** The player's HAND this tick: baseline jitter + a startle spike if a jolt is fresh. */
export function botMotion(s: GameState, p: BotPolicy, rng: Rng): number {
  const base = (1 - p.steadiness) * 0.12 * rng();
  let e = base;
  if (s.clock < s.startleUntil) {
    // A jolt just landed. A shaky hand jumps; a steady one barely does.
    const spike = s.startleK * (1 - p.steadiness) * p.startleReact * (0.6 + 0.8 * rng());
    e += spike;
  }
  return e;
}

export interface BotResult {
  score: number;
  banked: number; // orbs banked
  overflow: number;
  spill: number;
  orbsPlayed: number;
  ticks: number;
}

/** Run one full solo run under a policy + a hand seed. Deterministic. */
export function runBot(mode: Mode, seed: number, p: BotPolicy, handSeed: number): BotResult {
  const s = createGame(mode, seed);
  const rng = makeRng(handSeed);
  let ticks = 0;
  while (!s.over && ticks < 200_000) {
    ticks++;
    if (decideBank(s, p)) {
      release(s);
      continue;
    }
    step(s, botMotion(s, p, rng), DT);
  }
  let overflow = 0;
  let spill = 0;
  for (const r of s.results) {
    if (r.bustKind === 'overflow') overflow++;
    else if (r.bustKind === 'spill') spill++;
  }
  return {
    score: s.score,
    banked: s.results.filter((r) => !r.bust).length,
    overflow,
    spill,
    orbsPlayed: s.results.length,
    ticks,
  };
}

// Handy reference policies for the sim and the browser hook.
export const READER: BotPolicy = { bankAgitation: 0.82, steadiness: 0.82, startleReact: 0.8 };
export const GREEDY: BotPolicy = { bankAgitation: 0.985, steadiness: 0.82, startleReact: 0.8 };
export const TIMID: BotPolicy = { bankAgitation: 0.5, steadiness: 0.82, startleReact: 0.8 };
